'use client'

import { useState, useEffect, useCallback, useRef } from 'react'
import { useRouter } from 'next/navigation'
import { X, Share2, ArrowRight } from 'lucide-react'
import { BadgeIcon } from './badge-icon'
import { useUncelebratedBadges } from '@/hooks/badges/use-uncelebrated-badges'
import { useMarkCelebrated } from '@/hooks/badges/use-mark-celebrated'
import type { UserBadge } from '@/types/badges'

const CONFETTI_COLORS: Record<string, string[]> = {
  bronze: ['#CD7F32', '#B87333', '#D4A76A', '#E8C39E'],
  silver: ['#C0C0C0', '#A9A9A9', '#D3D3D3', '#E8E8E8'],
  gold: ['#FFD700', '#DAA520', '#FFC107', '#F5E6CC'],
}

/** Pre-generate confetti particle styles to avoid Math.random() in render */
function generateConfettiStyles(count: number) {
  return Array.from({ length: count }, (_, i) => ({
    left: `${Math.random() * 100}%`,
    width: 4 + Math.random() * 6,
    height: 4 + Math.random() * 6,
    borderRadius: i % 3 === 0 ? '50%' : '0',
    animationDelay: `${Math.random() * 2}s`,
    animationDuration: `${2 + Math.random() * 2}s`,
  }))
}

const CONFETTI_STYLES = generateConfettiStyles(40)

function ConfettiParticle({ color, index }: { color: string; index: number }) {
  const style = CONFETTI_STYLES[index]
  return (
    <div
      className="confetti-particle absolute top-0"
      style={{ ...style, backgroundColor: color }}
      aria-hidden="true"
    />
  )
}

interface CelebrationContentProps {
  badge: UserBadge
  index: number
  total: number
  onDismiss: () => void
}

function CelebrationContent({ badge, index, total, onDismiss }: CelebrationContentProps) {
  const router = useRouter()
  const cardRef = useRef<HTMLDivElement>(null)
  const confettiColors = CONFETTI_COLORS[badge.badge.tier] ?? CONFETTI_COLORS.bronze
  const [copied, setCopied] = useState(false)

  // Focus trap: focus the card on mount
  useEffect(() => {
    cardRef.current?.focus()
  }, [])

  const handleShare = useCallback(async () => {
    const text = `I just earned the "${badge.badge.name}" badge on PromptVan!`
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Clipboard failed silently
    }
  }, [badge.badge.name])

  const handleViewAll = useCallback(() => {
    router.push(`/u/me?tab=badges`)
    onDismiss()
  }, [router, onDismiss])

  // Focus trap: keep focus within the card
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key !== 'Tab') return
    const focusable = cardRef.current?.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
    if (!focusable?.length) return
    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault()
      first.focus()
    }
  }, [])

  return (
    <>
      {/* Confetti layer */}
      <div className="confetti-container pointer-events-none fixed inset-0 z-[61] overflow-hidden">
        {CONFETTI_STYLES.map((_, i) => (
          <ConfettiParticle key={i} color={confettiColors[i % confettiColors.length]} index={i} />
        ))}
      </div>

      {/* Content */}
      <div
        ref={cardRef}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        className="badge-celebration-card relative z-[62] flex max-w-sm flex-col items-center rounded-2xl bg-[var(--color-bg-surface)] p-8 shadow-2xl outline-none"
        role="alertdialog"
        aria-label="Achievement unlocked"
        aria-describedby="badge-celebration-desc"
        aria-modal="true"
      >
        <button
          type="button"
          onClick={onDismiss}
          className="absolute right-3 top-3 rounded-md p-1 text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] focus-visible:outline-2 focus-visible:outline-[var(--color-brand-indigo)]"
          aria-label="Dismiss"
        >
          <X className="h-5 w-5" />
        </button>

        {total > 1 && (
          <p className="mb-2 text-xs font-medium text-[var(--color-text-muted)]">
            Badge {index + 1} of {total}
          </p>
        )}

        <p className="mb-4 text-xs font-semibold uppercase tracking-wider text-[var(--color-brand-indigo)]">
          Achievement Unlocked
        </p>

        <BadgeIcon badge={badge.badge} size="xl" />

        <h2 id="badge-celebration-desc" className="mt-4 text-xl font-bold text-[var(--color-text-primary)]">
          {badge.badge.name}
        </h2>
        <p className="mt-1 text-center text-sm text-[var(--color-text-secondary)]">
          {badge.badge.description}
        </p>

        <div className="mt-6 flex items-center gap-3">
          <button
            type="button"
            onClick={handleShare}
            className="flex items-center gap-1.5 rounded-lg border border-[var(--color-border-default)] px-4 py-2 text-sm font-medium text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-bg-elevated)]"
          >
            <Share2 className="h-4 w-4" />
            {copied ? 'Copied!' : 'Share'}
          </button>
          <button
            type="button"
            onClick={handleViewAll}
            className="flex items-center gap-1.5 rounded-lg bg-[var(--color-brand-indigo)] px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90"
          >
            View All Badges
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </>
  )
}

export function BadgeCelebration() {
  const { data: uncelebrated } = useUncelebratedBadges()
  const markCelebrated = useMarkCelebrated()
  const [currentIndex, setCurrentIndex] = useState(0)
  const [visible, setVisible] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout>>(null)
  // Snapshot the queue so it doesn't change mid-celebration
  const queueRef = useRef<UserBadge[]>([])
  const celebratedRef = useRef(new Set<string>())

  const queue = queueRef.current

  // When new uncelebrated badges arrive, snapshot them and start showing
  useEffect(() => {
    if (!uncelebrated?.length) return
    // Only set up a new queue if we're not already showing one
    if (visible) return

    const newBadges = uncelebrated.filter((b) => !celebratedRef.current.has(b.id))
    if (!newBadges.length) return

    queueRef.current = newBadges
    setCurrentIndex(0)
    setVisible(true)
  }, [uncelebrated, visible])

  // Mark the CURRENT badge as celebrated when it becomes visible
  useEffect(() => {
    if (!visible || !queue.length || currentIndex >= queue.length) return
    const badge = queue[currentIndex]
    if (!celebratedRef.current.has(badge.id)) {
      celebratedRef.current.add(badge.id)
      markCelebrated.mutate(badge.id)
    }
  }, [visible, currentIndex]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleDismiss = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current)
    if (currentIndex < queue.length - 1) {
      setCurrentIndex((prev) => prev + 1)
    } else {
      setVisible(false)
    }
  }, [currentIndex, queue.length])

  // Auto-dismiss timer - pause on hover
  const [hovered, setHovered] = useState(false)
  useEffect(() => {
    if (!visible || hovered) return
    timerRef.current = setTimeout(handleDismiss, 8000)
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [visible, currentIndex, hovered, handleDismiss])

  // Escape key
  useEffect(() => {
    if (!visible) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleDismiss()
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [visible, handleDismiss])

  if (!visible || !queue.length || currentIndex >= queue.length) return null

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center"
      onClick={handleDismiss}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      role="presentation"
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" aria-hidden="true" />

      {/* Stop event propagation on card */}
      <div onClick={(e) => e.stopPropagation()}>
        <CelebrationContent
          badge={queue[currentIndex]}
          index={currentIndex}
          total={queue.length}
          onDismiss={handleDismiss}
        />
      </div>

      {/* CSS animations */}
      <style jsx global>{`
        @media (prefers-reduced-motion: no-preference) {
          .badge-celebration-card {
            animation: badge-scale-in 300ms cubic-bezier(0.34, 1.56, 0.64, 1) both;
          }
          @keyframes badge-scale-in {
            from { transform: scale(0.8); opacity: 0; }
            to { transform: scale(1); opacity: 1; }
          }
          .confetti-particle {
            animation: confetti-fall linear forwards;
          }
          @keyframes confetti-fall {
            0% { transform: translateY(-10vh) rotate(0deg); opacity: 1; }
            100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .confetti-container { display: none; }
          .badge-celebration-card { animation: none; }
        }
      `}</style>
    </div>
  )
}
